import { restApiAtom, useAddAlert, hubAtom } from '@Modules/hubs/atoms'
import {
  atomFamily,
  SetterOrUpdater,
  useRecoilCallback,
  useRecoilState,
  useRecoilValue,
} from 'recoil'
import { atom } from 'recoil'
import { presenterHub } from '@RestApi/types'
import { useIntl } from 'react-intl'
import messages from '../../messages'

export interface tagCount {
  name: string
  count: number
  visible: boolean
}

export interface collection {
  id: string
  name: string
  slug: string
  hub_id: string
  tags: string[] | null
  hidden: boolean
  index: number
  sort_content_by: string
  asset_display_count: number
  tag_counts: tagCount[] | null
  min_role: string
  archived: boolean
}

const emptyCollection: collection = {
  id: '',
  name: '',
  slug: '',
  hub_id: '',
  tags: null,
  hidden: false,
  index: 0,
  sort_content_by: '',
  asset_display_count: 0,
  tag_counts: null,
  min_role: '',
  archived: false,
}

export const collectionIdsAtom = atom<Array<string>>({
  key: 'collection.ids',
  default: [],
})

export const archivedCollectionIdsAtom = atom<Array<string>>({
  key: 'collection.archived.ids',
  default: [],
})

export const collectionDataAtom = atomFamily<collection, string>({
  key: 'collection.data',
  default: emptyCollection,
})

export const useRemoveCollection = () => {
  const restApi = useRecoilValue<any>(restApiAtom)
  const addAlert = useAddAlert()
  const intl = useIntl()
  const [collectionIds, setCollectionIds] = useRecoilState(collectionIdsAtom)
  const makeCurrentCollectionDefault = useMakeCurrentCollectionDefault()
  return useRecoilCallback(
    () => async (id: string, hub: presenterHub, onSuccess?: () => void) => {
      const filteredCollectionIds = collectionIds.filter((collectionId) => collectionId !== id)
      let newDefaultPosition = collectionIds.findIndex(
        (id) => id === hub.settings.default_collection_id
      )

      const resp = await restApi.DeleteCollection(hub.id, id)

      if (resp.error) {
        addAlert({
          message: intl.formatMessage(messages.collectionDeleteError),
          type: 'DANGER',
        })
        return
      }

      addAlert({
        message: intl.formatMessage(messages.collectionDeleted),
        type: 'SUCCESS',
      })
      setCollectionIds(filteredCollectionIds)

      // check if the deleted selection is a default selection
      if (id === hub.settings.default_collection_id) {
        if (newDefaultPosition === collectionIds.length - 1) {
          newDefaultPosition--
        }
        makeCurrentCollectionDefault(hub, filteredCollectionIds[newDefaultPosition])
      }

      onSuccess && onSuccess()
    },
    [collectionIds]
  )
}

export const useArchiveCollection = () => {
  const restApi = useRecoilValue<any>(restApiAtom)
  const collectionIds = useRecoilValue(collectionIdsAtom)
  const makeCurrentCollectionDefault = useMakeCurrentCollectionDefault()
  const addAlert = useAddAlert()
  const intl = useIntl()

  return useRecoilCallback(
    ({ set }) =>
      async (id: string, hub: presenterHub, collection: collection, onSuccess?: () => void) => {
        const filteredCollectionIds = collectionIds.filter((collectionId) => collectionId !== id)
        let newDefaultPosition = collectionIds.findIndex(
          (id) => id === hub.settings.default_collection_id
        )

        const archivedCollection = { ...collection, archived: true }
        const resp = await restApi.updateCollection(hub.id, id, archivedCollection)

        if (resp.error) {
          addAlert({
            message: intl.formatMessage(messages.collectionArchiveError),
            type: 'DANGER',
          })
          return
        }

        addAlert({
          message: intl.formatMessage(messages.collectionArchived),
          type: 'SUCCESS',
        })

        set(collectionDataAtom(id), archivedCollection)
        set(collectionIdsAtom, (collectionIds) =>
          collectionIds.filter((collectionId) => collectionId !== id)
        )
        set(archivedCollectionIdsAtom, (archivedCollectionIds) => [id, ...archivedCollectionIds])

        if (id === hub.settings.default_collection_id) {
          if (newDefaultPosition === collectionIds.length - 1) {
            newDefaultPosition--
          }
          makeCurrentCollectionDefault(hub, filteredCollectionIds[newDefaultPosition])
        }
        onSuccess && onSuccess()
      },
    []
  )
}

export const useRestoreCollection = () => {
  const restApi = useRecoilValue<any>(restApiAtom)
  const currentCollectionIds = useRecoilValue<any>(collectionIdsAtom)
  const addAlert = useAddAlert()
  const intl = useIntl()

  return useRecoilCallback(
    ({ set }) =>
      async (id: string, hub: presenterHub, collection: collection, onSuccess?: () => void) => {
        const restoredCollection = { ...collection, archived: false }
        const resp = await restApi.updateCollection(hub.id, id, restoredCollection)

        if (resp.error) {
          addAlert({
            message: intl.formatMessage(messages.collectionRestoreError),
            type: 'DANGER',
          })
          return
        }

        addAlert({
          message: intl.formatMessage(messages.collectionRestored),
          type: 'SUCCESS',
        })

        set(collectionDataAtom(id), restoredCollection)
        set(archivedCollectionIdsAtom, (archivedCollectionIds) =>
          archivedCollectionIds.filter((archivedCollectionId) => archivedCollectionId !== id)
        )
        set(collectionIdsAtom, (collectionIds) => [...collectionIds, id])
        await restApi.changeOrder(hub.id, [...currentCollectionIds, id])
        onSuccess && onSuccess()
      },
    []
  )
}

export const useCreateCollection = () => {
  const restApi = useRecoilValue<any>(restApiAtom)
  const addAlert = useAddAlert()
  const intl = useIntl()
  const [collectionIds, setCollectionIds] = useRecoilState(collectionIdsAtom)

  return useRecoilCallback(
    ({ set }) =>
      async (
        hub: presenterHub,
        collectionName: string,
        setSpinner?: React.Dispatch<React.SetStateAction<boolean>>,
        onSuccess?: () => void
      ) => {
        setSpinner && setSpinner(true)
        const resp = await restApi.AddCollection(hub.id, {
          name: collectionName,
          slug: '',
          tag_counts: [],
          tags: [],
        })
        setSpinner && setSpinner(false)
        if (resp.error) {
          addAlert({
            message: intl.formatMessage(messages.collectionCreateError),
            type: 'DANGER',
          })
          return
        }

        addAlert({
          message: intl.formatMessage(messages.collectionAdded),
          type: 'SUCCESS',
        })

        set(collectionDataAtom(resp.data.id), resp.data)
        setCollectionIds([resp.data.id, ...collectionIds])
        await restApi.changeOrder(hub.id, [...collectionIds, resp.data.id])
        onSuccess && onSuccess()
      },
    []
  )
}

type SwapArray = <T>(currentOrder: number, nextOrder: number, targetedArray: Array<T>) => Array<T>

export const swapArrayElements: SwapArray = (currentOrder, nextOrder, targetedArray) => {
  if (currentOrder == nextOrder) {
    return targetedArray
  }
  const swappedArray = [...targetedArray]
  const collectionIdToReplace = swappedArray[currentOrder]
  swappedArray[currentOrder] = swappedArray[nextOrder]
  swappedArray[nextOrder] = collectionIdToReplace

  return swappedArray
}

type Direction = 'up' | 'down'

export const useSwapCollection = () => {
  const restApi = useRecoilValue<any>(restApiAtom)
  const [collectionIds, setCollectionIds] = useRecoilState(collectionIdsAtom)
  const addAlert = useAddAlert()
  const intl = useIntl()

  return useRecoilCallback(() => async (order: number, hub: presenterHub, direction: Direction) => {
    let swappedArray: string[]
    if (direction === 'up') {
      swappedArray = swapArrayElements(order, order - 1, collectionIds)
    } else {
      swappedArray = swapArrayElements(order, order + 1, collectionIds)
    }
    const resp = await restApi.changeOrder(hub.id, swappedArray)
    if (resp.error) {
      addAlert({
        message: intl.formatMessage(messages.collectionReorderError),
        type: 'DANGER',
      })
      return
    }
    setCollectionIds(swappedArray)
  })
}

export const useToggleAllCollection = () => {
  const restApi = useRecoilValue<any>(restApiAtom)
  const addAlert = useAddAlert()
  const intl = useIntl()

  return useRecoilCallback(
    ({ set }) =>
      async (hub: presenterHub, hideAllCollection: boolean, collectionId?: string) => {
        const updatedHub = {
          ...hub,
          settings: {
            ...hub.settings,
            hide_all_collection: !hideAllCollection,
            ...(!hideAllCollection && collectionId && { default_collection_id: collectionId }),
          },
        }
        const resp = await restApi.updateHub(hub.id, updatedHub)

        if (resp.error) {
          addAlert({
            message: intl.formatMessage(messages.errorTogglingAllCollection),
            type: 'DANGER',
          })
          return
        }

        addAlert({
          message: intl.formatMessage(messages.successfullyToggledALLCollection),
          type: 'SUCCESS',
        })
        set(hubAtom, resp)
      },
    []
  )
}

export const useUpdateCollection = () => {
  const restApi = useRecoilValue<any>(restApiAtom)
  const addAlert = useAddAlert()
  const intl = useIntl()

  return useRecoilCallback(
    () =>
      async (
        hub: presenterHub,
        collectionId: string,
        updatedCollection: collection,
        setCollection: SetterOrUpdater<collection>,
        setInitialCollection: React.Dispatch<React.SetStateAction<collection>>,
        onSuccess?: () => void
      ) => {
        const resp = await restApi.updateCollection(hub.id, collectionId, updatedCollection)
        if (resp.error) {
          addAlert({
            message: intl.formatMessage(messages.collectionSaveError),
            type: 'DANGER',
          })
          return
        }
        addAlert({
          message: intl.formatMessage(messages.collectionSaved),
          type: 'SUCCESS',
        })
        setCollection(updatedCollection)
        setInitialCollection(updatedCollection)
        onSuccess && onSuccess()
      },
    []
  )
}

export const useMakeCurrentCollectionDefault = () => {
  const restApi = useRecoilValue<any>(restApiAtom)
  const addAlert = useAddAlert()
  const intl = useIntl()
  return useRecoilCallback(
    ({ set }) =>
      async (hub: presenterHub, collectionId: string | null, setIsClicked?: any) => {
        const resp = await restApi.updateHub(hub.id, {
          ...hub,
          settings: {
            ...hub.settings,
            default_collection_id: collectionId,
          },
        })
        if (resp.error) {
          addAlert({
            message: intl.formatMessage(messages.errorMakeCollectionDefault),
            type: 'DANGER',
          })
          return
        }
        addAlert({
          message: intl.formatMessage(messages.successfullyMakeCollectionDefault),
          type: 'SUCCESS',
        })
        setIsClicked && setIsClicked(true)
        set(hubAtom, resp)
      },
    []
  )
}

export const useFetchTags = () => {
  const restApi = useRecoilValue<any>(restApiAtom)
  const addAlert = useAddAlert()
  const intl = useIntl()

  return useRecoilCallback(
    ({ set }) =>
      async (hub: presenterHub, collectionId: string) => {
        const resp = await restApi.fetchCollection(hub.id, collectionId)
        if (resp.error) {
          addAlert({
            message: intl.formatMessage(messages.fetchCollectionError),
            type: 'DANGER',
          })
          return
        }
        set(collectionDataAtom(collectionId), resp.data)
      },
    []
  )
}
